

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from 'react-confetti';

import Panels from "@/components/Panels";
import NavHeader from "@/components/NavHeader";
import EmailAuth from "./AuthSignupEmail";
import GoogleAuth from "./AuthSignupGoogle";
import UnderAge from "./AuthUnderAge";
import Questionairre from "./FinishedQuestionairre";
import ThankYou from "./FinishedThankYou";
import Logins from "./AuthLogins";
import Welcome from "./Welcome";
import QuickSettings from "./QuickSettings";
import NoAccount from "./NoAccount";
import { CongratulationsImage, DiscordImage, Image3, Image5, JoinStudentsImage, ThemeImage, TiktokImage, TrialTimelineImage, TrialTimelineImageSkipped } from "@/components/SidePictures";
import Questionairre2 from "./FinishedQuestionairre2";
import Questionairre3 from "./FinishedQuestionairre3";
import MagicLink from "./AuthSignupMagicLink";
import OutlookAuth from "./AuthSignupOutlook";
import { TrySync } from "./TrySync";
import { CommunicationProvider } from "@/lib/CommunicationWrapper";
import { useAuthStep, AuthStepProvider } from "@/hooks/useAuthStep";
import { ClaimTrial } from "./ClaimTrial";
import { TrialClaimed } from "./TrialClaimed";
import { PreviouslyAppliedTheme } from "./PreviouslyAppliedTheme";


const ExitButton = ({ onClick }: { onClick: () => void }) => {

    return (
        <button onClick={onClick} className="z-30 shadow-lg absolute top-[10px] right-[10px] bg-white rounded-full p-[10px] hover:scale-108 transition-all duration-200">
            <X className="w-[20px] h-[20px]" />
        </button>
    )
}

export const OnboardingContent = () => {
    const { authStep, setAuthStep, showConfetti, setShowConfetti } = useAuthStep();

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // if none of the handleBack/handkeSkip need any extra proecessing in their functions
    // can make it just a string for the step it needs to go to
    const steps: Record<string, any> = {
        welcome: {
            left: Welcome,
            right: Image3,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        quickSettings: {
            left: QuickSettings,
            right: null,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        logins: {
            left: Logins,
            right: JoinStudentsImage,
            handleBack: null,
            handleSkip: null,
            exit: () => setAuthStep("noAccount"),
        },
        googleSignup: {
            left: GoogleAuth,
            right: JoinStudentsImage,
            handleBack: () => setAuthStep("logins"),
            handleSkip: null,
            exit: () => setAuthStep("noAccount"),
        },
        outlookSignup: {
            left: OutlookAuth,
            right: JoinStudentsImage,
            handleBack: () => setAuthStep("logins"),
            handleSkip: null,
            exit: () => setAuthStep("noAccount"),
        },
        emailSignup: {
            left: EmailAuth,
            right: JoinStudentsImage,
            handleBack: () => setAuthStep("logins"),
            handleSkip: null,
            exit: () => setAuthStep("noAccount"),
        },
        magicLinkSent: {
            left: MagicLink,
            right: JoinStudentsImage,
            handleBack: () => setAuthStep("emailSignup"),
            handleSkip: null,
            exit: () => setAuthStep("noAccount"),
        },
        underAge: {
            left: UnderAge,
            right: Image5,
            handleBack: () => setAuthStep("logins"),
            handleSkip: null,
            exit: null,
        },
        questionairre: {
            left: Questionairre,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        questionairre3: {
            left: Questionairre3,
            right: DiscordImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        thankYou: {
            left: ThankYou,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null /*() => window?.top?.postMessage({ type: "EXIT_ONBOARDING", payload: { target: "menu", code: false } }, "*"),*/
        },
        noAccount: {
            left: NoAccount,
            right: JoinStudentsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        sync: {
            left: TrySync,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        claimTrial: {
            left: ClaimTrial,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        trialClaimed: {
            left: TrialClaimed,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },
        previouslyAppliedTheme: {
            left: PreviouslyAppliedTheme,
            right: CongratulationsImage,
            handleBack: null,
            handleSkip: null,
            exit: null,
        },

    };

    const LeftComponent = steps[authStep]?.left;
    const RightComponent = steps[authStep]?.right;

    return (
        <>
            {showConfetti && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={200}
                    gravity={0.2}
                    initialVelocityY={-10}
                    wind={0.02}
                    tweenDuration={1500}
                    onConfettiComplete={() => setShowConfetti(false)}
                />
            )}
            <Panels>
                <div className="flex-1 flex flex-col h-full w-full overflow-y-auto">
                    {steps[authStep]?.exit && <ExitButton onClick={steps[authStep]?.exit} />}
                    <AnimatePresence mode="wait">
                        {LeftComponent && (
                            <motion.div
                                key={authStep}
                                initial={{ x: 40, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -40, opacity: 0 }}
                                transition={{ duration: 0.17, ease: "easeOut" }}
                                className="w-full h-full flex flex-col"
                            >
                                <NavHeader
                                    handleBack={steps[authStep]?.handleBack}
                                    handleSkip={steps[authStep]?.handleSkip}
                                />
                                <LeftComponent />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {RightComponent && (
                    <RightComponent />
                )}
            </Panels>
        </>
    );


};

const SignupLayoutContent = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {

        window?.top?.postMessage({ type: "LOAD_COMPLETE" }, "*");

        // Trigger fade-in after component mounts
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);


        return () => clearTimeout(timer);
    }, []);

    return (
        <div className={`relative h-full max-w-[1000px] h-screen mx-auto transition-opacity duration-1000 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <OnboardingContent />
        </div>
    );
};

export const SignupLayout = () => {

    const params = new URLSearchParams(window.location.search ?? "");
    const skipWelcome = params.get("skipWelcome") === "true";

    return (
        <CommunicationProvider>
            <AuthStepProvider initial={{ page: skipWelcome ? "logins" : "welcome", step: skipWelcome ? "logins" : "welcome" }}>
                <SignupLayoutContent />
            </AuthStepProvider>
        </CommunicationProvider>
    );
};

export default SignupLayout;